/**
 * Hook for managing HeyReach integration status.
 * 
 * Provides:
 * - Connection status checking
 * - Loading state management
 * - Auto-refresh on mount
 */

import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

/**
 * Custom hook to manage HeyReach integration status.
 * 
 * @returns {Object} Integration state and methods
 * @returns {boolean} isConnected - Whether HeyReach is connected
 * @returns {boolean} isLoading - Whether status is being fetched
 * @returns {string|null} error - Error message if any
 * @returns {Function} refreshStatus - Function to manually refresh status
 */
const useHeyreachIntegration = () => {
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  /**
   * Fetch current HeyReach integration status from the API.
   */
  const refreshStatus = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await api.get('/api/integrations/heyreach/status');
      const status = response.data?.status;
      
      // Consider 'active' status as connected
      setIsConnected(status === 'active');
    } catch (err) {
      // 404 means not connected, which is valid state
      if (err.response?.status === 404) {
        setIsConnected(false);
      } else {
        console.error('Failed to fetch HeyReach status:', err);
        setError(err.response?.data?.detail || 'Failed to check HeyReach status');
        setIsConnected(false);
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Fetch status on mount
  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  // Listen for integration changes (e.g., after connecting/disconnecting)
  useEffect(() => {
    const handleIntegrationChange = () => {
      refreshStatus();
    };

    window.addEventListener('heyreachIntegrationChanged', handleIntegrationChange);
    
    return () => {
      window.removeEventListener('heyreachIntegrationChanged', handleIntegrationChange);
    };
  }, [refreshStatus]);

  return {
    isConnected,
    isLoading,
    error,
    refreshStatus,
  };
};

export default useHeyreachIntegration;
